"use client";

import type { IconicVoice, IconicVoicesSliderBlock } from "@mw/shared/payload-types";

import { AudioProvider } from "@/app/_providers/AudioProvider";
import { AspectRatio } from "@components/core/AspectRatio";
import { Image } from "@components/core/Image";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";
import { RequestVoiceDialog } from "@ui/components/core/RequestVoiceDialog";
import { ImageSizes } from "@ui/lib/image";
import { filterEntitiesArray } from "@utilities/payload";
import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { Block } from "./Block";

const BlockIconicVoicesSliderLayout = ({ voices }: IconicVoicesSliderBlock) => {
  const [selectedVoice, setSelectedVoice] = useState<IconicVoice | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!voices || voices.length === 0) return null;

  const handleVoiceClick = (voice: IconicVoice) => {
    setSelectedVoice(voice);
    setIsDialogOpen(true);
  };

  const filteredVoices = filterEntitiesArray(voices);

  const renderVoiceItem = (voice: IconicVoice) => {
    const { name, shortBio, image } = voice;

    return (
      <button
        className='group relative w-full overflow-hidden rounded-xl text-left transition-shadow duration-300 ease-out hover:shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)]'
        onClick={() => handleVoiceClick(voice)}
        type='button'
      >
        <AspectRatio className='bg-darker' ratio='4x3' rounded={false}>
          <Image resource={image} sizes={ImageSizes.FULL_1_3_ON_LG} className='object-top' />
        </AspectRatio>
        <div className='effect-color space-y-4 bg-dark px-8 pb-40 pt-28 text-center group-hover:bg-default'>
          <p className='f-paragraph-01'>{name}</p>
          {shortBio && <p className='f-paragraph-03 line-clamp-2 text-light'>{shortBio}</p>}
        </div>
        <div className='effect-opacity absolute right-8 top-8 flex h-32 w-32 -rotate-45 items-center justify-center rounded-full bg-default opacity-0 shadow-outline group-hover:opacity-100'>
          <ChevronRight size={20} />
        </div>
      </button>
    );
  };

  return (
    <Block name='iconic-voices-slider'>
      <ScrollGrid items={filteredVoices} renderItem={renderVoiceItem} />

      {/* Request Voice Dialog */}
      {selectedVoice && <RequestVoiceDialog onOpenChange={setIsDialogOpen} open={isDialogOpen} voice={selectedVoice} />}
    </Block>
  );
};

const BlockIconicVoicesSlider = (props: IconicVoicesSliderBlock) => {
  return (
    <AudioProvider>
      <BlockIconicVoicesSliderLayout {...props} />
    </AudioProvider>
  );
};

BlockIconicVoicesSlider.displayName = "BlockIconicVoicesSlider";

export { BlockIconicVoicesSlider };
